import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Settings } from 'lucide-react';
import { TimestampCell } from '../../components/common/display/TimestampCell';
import { ElapsedCell } from '../../components/common/display/ElapsedCell';
import { useJobs, useWorkflowConfigs, useDiscoveredWorkflows } from '../../api/workflows';
import { useAuth } from '../../hooks/useAuth';
import { useNamespace } from '../../hooks/useNamespace';
import { useWorkflowListEvents } from '../../hooks/useEventHooks';
import { useFilterParams } from '../../hooks/useFilterParams';
import { buildApiPath } from '../../lib/api-path';
import { DataTable, type Column } from '../../components/common/data/DataTable';
import { WorkflowPill } from '../../components/common/display/WorkflowPill';
import { PageHeader } from '../../components/common/layout/PageHeader';
import { FilterBar, FilterSelect } from '../../components/common/data/FilterBar';
import { StickyPagination } from '../../components/common/data/StickyPagination';
import { ListToolbar } from '../../components/common/data/ListToolbar';
import { RowAction, RowActionGroup } from '../../components/common/layout/RowActions';
import type { LTJob, WorkflowTier } from '../../api/types';

export type ExecutionsTier = 'all' | 'certified' | 'durable';

const jobStatusMap: Record<string, string> = {
  running: 'in_progress',
  completed: 'completed',
  failed: 'failed',
};

const STATUS_DOT: Record<string, string> = {
  in_progress: 'bg-status-active',
  completed: 'bg-status-success',
  failed: 'bg-status-error',
};

const STATUS_COLORS: Record<string, string> = {
  running: 'text-status-active',
  completed: 'text-status-success',
  failed: 'text-status-error',
};

function buildColumns(
  onFilterEntity: (entity: string) => void,
  onFilterStatus: (status: string) => void,
  isSuperAdmin: boolean,
  navigate: (path: string) => void,
  tierMap: Map<string, WorkflowTier>,
): Column<LTJob>[] {
  return [
    {
      key: 'workflow_id',
      label: 'Workflow ID / Type',
      render: (row) => {
        const dotClass = STATUS_DOT[jobStatusMap[row.status] ?? row.status] ?? 'bg-status-pending';
        const pulseClass = row.status === 'running' ? ' animate-pulse' : '';
        return (
          <div className="flex items-start gap-2 min-w-0">
            <span className={`w-1.5 h-1.5 shrink-0 rounded-full mt-1.5 ${dotClass}${pulseClass}`} title={row.status} />
            <div className="min-w-0">
              <span className="font-mono text-xs text-text-primary truncate block">
                {row.workflow_id}
              </span>
              <div className="mt-0.5">
                <WorkflowPill type={row.entity} variant={tierMap.get(row.entity) ?? 'durable'} size="xs" />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => <TimestampCell date={row.created_at} />,
      className: 'w-40',
      sortable: true,
    },
    {
      key: 'updated_at',
      label: 'Updated',
      render: (row) => <TimestampCell date={row.updated_at} />,
      className: 'w-40',
      sortable: true,
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (row) => (
        <ElapsedCell
          startDate={row.created_at}
          endDate={row.status === 'running' ? null : row.updated_at}
          isLive={row.status === 'running'}
        />
      ),
      className: 'w-28',
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <RowActionGroup>
          <RowAction
            icon={Filter}
            title={`Filter by ${row.entity}`}
            onClick={() => onFilterEntity(row.entity)}
          />
          <button
            onClick={(e) => { e.stopPropagation(); onFilterStatus(row.status); }}
            className="opacity-0 group-hover/row:opacity-100 transition-opacity"
            title={`Filter by ${row.status}`}
          >
            <svg className={`w-[18px] h-[18px] ${STATUS_COLORS[row.status] ?? 'text-text-tertiary'} hover:opacity-70`} viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="6" />
            </svg>
          </button>
          {isSuperAdmin && (
            <RowAction
              icon={Settings}
              title="View config"
              onClick={() => navigate(`/workflows/registry/${encodeURIComponent(row.entity)}`)}
            />
          )}
        </RowActionGroup>
      ),
      className: 'w-24 text-right',
    },
  ];
}

export function WorkflowsDashboard({ tier: initialTier = 'all' }: { tier?: ExecutionsTier }) {
  useWorkflowListEvents();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { namespace } = useNamespace();

  const { filters, setFilter, pagination, sort, setSort } = useFilterParams({
    filters: { search: '', entity: '', status: '', tier: initialTier },
  });

  const activeTier = (filters.tier || 'all') as ExecutionsTier;

  // Map tier to server-side registered filter
  const registeredFilter = activeTier === 'certified' ? 'true'
    : activeTier === 'durable' ? 'false'
    : undefined;

  const { data: configs } = useWorkflowConfigs();
  const { data: discoveredData } = useDiscoveredWorkflows();

  const tierMap = useMemo(() => {
    const map = new Map<string, WorkflowTier>();
    for (const dw of discoveredData ?? []) {
      map.set(dw.workflow_type, dw.tier ?? 'durable');
    }
    return map;
  }, [discoveredData]);

  const columns = buildColumns(
    (entity) => setFilter('entity', entity),
    (status) => setFilter('status', status),
    isSuperAdmin,
    navigate,
    tierMap,
  );
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    if (searchInput === filters.search) return;
    const timer = setTimeout(() => setFilter('search', searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, setFilter, filters.search]);

  const { data: jobsData, isLoading, refetch, isFetching } = useJobs({
    limit: pagination.pageSize,
    offset: pagination.offset,
    entity: filters.entity || undefined,
    search: filters.search || undefined,
    status: filters.status || undefined,
    sort_by: sort.sort_by || 'created_at',
    order: sort.order || 'desc',
    registered: registeredFilter,
    namespace: namespace || undefined,
  });

  const total = jobsData?.total ?? 0;
  const jobs = jobsData?.jobs ?? [];

  const entities = useMemo(() => {
    return [...new Set((configs ?? []).map((c) => c.workflow_type))].sort();
  }, [configs]);

  const pageTitle = 'Workflow Executions';

  const emptyMessage = activeTier === 'certified'
    ? 'No certified workflow executions found'
    : activeTier === 'durable'
      ? 'No durable workflow executions found'
      : 'No workflow executions found';

  return (
    <div>
      <PageHeader title={pageTitle} docsHash="#docs:dashboard.md:durable-executions" />

      <FilterBar actions={
        <ListToolbar
          onRefresh={() => refetch()}
          isFetching={isFetching}
          apiPath={buildApiPath('/workflow-states/jobs', {
            limit: pagination.pageSize,
            offset: pagination.offset,
            entity: filters.entity || undefined,
            search: filters.search || undefined,
            status: filters.status || undefined,
            sort_by: sort.sort_by || 'created_at',
            order: sort.order || 'desc',
            registered: registeredFilter,
            namespace: namespace || undefined,
          })}
        />
      }>
        <input
          type="text"
          placeholder="Search workflow ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="input text-[11px] py-1 px-2 w-56"
        />
        <FilterSelect
          label="Type"
          value={filters.entity}
          onChange={(v) => setFilter('entity', v)}
          options={entities.map((e) => ({ value: e, label: e }))}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={[
            { value: 'running', label: 'Running' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
        <FilterSelect
          label="Tier"
          value={filters.tier === 'all' ? '' : filters.tier}
          onChange={(v) => setFilter('tier', v || 'all')}
          options={[
            { value: 'certified', label: 'Certified' },
            { value: 'durable', label: 'Durable' },
          ]}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={jobs}
        keyFn={(row) => row.workflow_id}
        onRowClick={(row) => navigate(`/workflows/executions/${row.workflow_id}`)}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        sort={sort}
        onSort={setSort}
      />

      <StickyPagination
        page={pagination.page}
        totalPages={pagination.totalPages(total)}
        onPageChange={pagination.setPage}
        total={total}
        pageSize={pagination.pageSize}
        onPageSizeChange={pagination.setPageSize}
      />
    </div>
  );
}
